import { useCallback, useEffect, useRef, useState } from 'react';

import { probeReferenceVideo } from './reference-video';
import { StudioBoard } from './studio-contract';
import { useStudioApi } from './use-studio-api';

export type ReferenceStatus = 'idle' | 'probing' | 'uploading' | 'reading' | 'ready' | 'error';

/** What is known about a reference while it is being read, before the board has it. */
export interface ReferenceDraft {
    kind: 'video' | 'image';
    /** The file itself, shown from the browser — no wait for the upload. */
    previewUrl: string;
    /** One frame per shot, as soon as the cuts are found. */
    frameUrls: string[];
    aspect?: number;
    durationSec?: number;
}

/**
 * Bring a reference in: the reel or picture the vendor wants theirs to look
 * like.
 *
 * The browser does the measuring — where the cuts are, one frame per shot —
 * because it has the file already and does it in a second or two; the API
 * then has Gemini describe exactly those shots. The frames are kept: each one
 * is a framing a shot can copy.
 *
 * While it is read, `draft` holds the file and its frames as local pictures,
 * so the page can show the reference being studied from the first second.
 */
export function useReference(opts: {
    projectUuid?: string;
    setBoard: (u: (b: StudioBoard) => StudioBoard) => void;
}) {
    const api = useStudioApi();
    const [status, setStatus] = useState<ReferenceStatus>('idle');
    const [error, setError] = useState<string | null>(null);
    const [name, setName] = useState<string>('');
    const [draft, setDraft] = useState<ReferenceDraft | null>(null);
    const urls = useRef<string[]>([]);
    const release = () => { urls.current.forEach((u) => URL.revokeObjectURL(u)); urls.current = []; };
    useEffect(() => release, []);

    const attach = useCallback(async (file: File): Promise<StudioBoard['reference'] | null> => {
        const { projectUuid, setBoard } = opts;
        if (!projectUuid) return null;
        setError(null);
        setName(file.name);
        const isVideo = file.type.startsWith('video/') || /\.(mp4|mov|webm)$/i.test(file.name);
        release();
        const previewUrl = URL.createObjectURL(file);
        urls.current.push(previewUrl);
        setDraft({ kind: isVideo ? 'video' : 'image', previewUrl, frameUrls: isVideo ? [] : [previewUrl] });
        try {
            let reference: NonNullable<StudioBoard['reference']>;
            if (isVideo) {
                setStatus('probing');
                const probe = await probeReferenceVideo(file);
                const frameUrls = probe.frames.map((f) => { const u = URL.createObjectURL(f.blob); urls.current.push(u); return u; });
                setDraft((d) => (d ? { ...d, frameUrls, aspect: probe.width && probe.height ? probe.width / probe.height : undefined, durationSec: probe.durationSec } : d));
                setStatus('uploading');
                const asset: any = await api.importVideo(projectUuid, file, {
                    width: probe.width, height: probe.height, durationSec: probe.durationSec, thumbnail: probe.poster, origin: 'upload',
                });
                const frames = await Promise.all(probe.frames.map(async (f, i) => {
                    const up = await api.uploadStyleRef(new File([f.blob], `ref-frame-${i + 1}.jpg`, { type: 'image/jpeg' }));
                    return { t: f.t, url: up.url };
                }));
                reference = { kind: 'video', url: asset.url, cuts: probe.cuts, frames, durationSec: probe.durationSec };
            } else {
                setStatus('uploading');
                const up = await api.uploadStyleRef(file);
                reference = { kind: 'image', url: up.url, frames: [{ t: 0, url: up.url }] };
            }
            setBoard((b) => ({ ...b, reference }));
            setStatus('reading');
            const analysis = await api.analyzeReference({
                url: reference.url, kind: reference.kind, cuts: reference.cuts, frames: reference.frames, durationSec: reference.durationSec,
            });
            const done = { ...reference, analysis };
            setBoard((b) => ({ ...b, reference: done }));
            setStatus('ready');
            return done;
        } catch (err: any) {
            setStatus('error');
            setError(err?.response?.data?.message || err?.message || 'Could not read that file');
            return null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, opts]);

    const clear = useCallback(() => {
        opts.setBoard((b) => ({ ...b, reference: undefined }));
        setStatus('idle');
        setName('');
        setDraft(null);
        release();
    }, [opts]);

    return { status, error, name, draft, attach, clear, busy: status === 'probing' || status === 'uploading' || status === 'reading' };
}
